import { get, set } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { updateComponent } from '~/app/components/dynamic-grid/dynamic-grid-helpers';
import { useBuilder } from '~/app/lib/redux/selectors/builder-selector';
import { useDynamicGrid } from '~/app/lib/redux/selectors/dynamic-grid-selector';

export const MAX_RATING = 5;
export const MIN_RATING = 1;

export const STAR_RATING_SOURCE_TYPES = {
  CUSTOM: 'custom',
  MARKETPLACE_OFFER: 'marketplace-offer',
};

export const useDynamicStarRating = () => {
  const dispatch = useDispatch();
  const [marketplaceOfferId, setMarketplaceOfferId] = useState<string>('');
  const [rating, setRating] = useState<number>(MIN_RATING);
  const [starRatingSource, setStarRatingSource] = useState<string>('');
  const [layout, setLayout] = useState<
    'stacked' | 'big-number' | 'numeric' | undefined
  >(undefined);

  const { flatOutline, selected: selectedComponentId } = useBuilder();
  const { componentPath, marketplaceOffers } = useDynamicGrid({
    componentId: selectedComponentId,
  });
  const currentComponent = flatOutline?.[selectedComponentId];

  const handleUpdateComponent = useCallback(
    (field: string, value: string | number | undefined) => {
      set(currentComponent, `props.${componentPath}.${field}`, value);
      dispatch(updateComponent(currentComponent));
    },
    [currentComponent, componentPath, dispatch]
  );

  const handleChangeRating = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    if (value > MAX_RATING || value < MIN_RATING) {
      // Keep the rating between 0 and 5
      return;
    }
    setRating(value);
    handleUpdateComponent('starRating', value);
  };

  const handleChangeLayout = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as
      | 'stacked'
      | 'big-number'
      | 'numeric'
      | undefined;
    setLayout(value);
    handleUpdateComponent('starRatingLayout', value);
  };

  const handleMarketplaceOfferChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setMarketplaceOfferId(e.target.value);
    handleUpdateComponent('marketplaceOfferId', e.target.value);
  };

  const starRatingSourceOptions = useCallback(() => {
    const options = [
      {
        label: 'Custom Value',
        value: STAR_RATING_SOURCE_TYPES.CUSTOM,
      },
    ];

    if (!marketplaceOffers?.length) {
      return options;
    }

    options.push({
      label: 'Marketplace Offer',
      value: STAR_RATING_SOURCE_TYPES.MARKETPLACE_OFFER,
    });

    return options;
  }, [marketplaceOffers]);

  const marketplaceOfferOptions = useCallback(() => {
    const options = marketplaceOffers?.map((offer: any) => ({
      label: offer.name,
      value: offer.id,
    }));
    options.unshift({
      label: 'Select a Marketplace Offer',
      value: '',
    });
    return options;
  }, [marketplaceOffers]);

  const handleStarRatingSourceChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setStarRatingSource(e.target.value);
    handleUpdateComponent('starRatingSource', e.target.value);
  };

  useEffect(() => {
    setRating(
      get(currentComponent, `props.${componentPath}.starRating`, MIN_RATING)
    );
    setLayout(
      get(
        currentComponent,
        `props.${componentPath}.starRatingLayout`,
        undefined
      )
    );
    setMarketplaceOfferId(
      get(currentComponent, `props.${componentPath}.marketplaceOfferId`, '')
    );
    setStarRatingSource(
      get(
        currentComponent,
        `props.${componentPath}.starRatingSource`,
        STAR_RATING_SOURCE_TYPES.CUSTOM
      )
    );
  }, [
    currentComponent,
    componentPath,
    setLayout,
    setRating,
    setStarRatingSource,
    setMarketplaceOfferId,
  ]);

  return {
    rating,
    layout,
    marketplaceOfferId,
    starRatingSource,
    handleChangeRating,
    handleChangeLayout,
    handleMarketplaceOfferChange,
    starRatingSourceOptions,
    marketplaceOfferOptions,
    handleStarRatingSourceChange,
  };
};
